import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { ContractTaskDto, bffApi } from "../../../../../autogen/bff-api";
import { isNoAccessError } from "../../../../../common/errors/isNoAccessError";
import { useRumError } from "../../../../../common/errors/RumComponentContext/useRumError";
import { useAppDispatch } from "../../../../../common/redux/hooks";
import { ChangeList } from "./changes/ChangeList";
import { Comments } from "./comments/Comments";
import { Description } from "./description/Description";
import { DueDate } from "./due-date/DueDate";
import { ResponsiblePerson } from "./responsible-person/ResponsiblePerson";
import { Status } from "./status/Status";
import { Title } from "./title/Title";
import { useContractTaskEditor } from "./useContractTaskEditor";

interface Props {
  taskId: string;
  onClose: () => void;
}

export const ShowTaskModal = ({ taskId, onClose }: Props) => {
  const dispatch = useAppDispatch();
  const rumError = useRumError();
  const [task, setTask] = useState<ContractTaskDto | undefined>();
  const [loadFailure, setLoadFailure] = useState<LoadFailure | undefined>();

  const fetch = useCallback(async (): Promise<ContractTaskDto | undefined> => {
    return dispatch(bffApi.endpoints.getTask.initiate({ taskId }))
      .unwrap()
      .catch((e) => {
        if (isNoAccessError(e)) {
          setLoadFailure("noAccess");
        } else {
          rumError(e, { message: "Contract task could not be loaded" });
          setLoadFailure("failed");
        }
        return undefined;
      });
  }, [dispatch, rumError, taskId]);

  useEffect(() => {
    const load = async () => {
      const value = await fetch();
      setTask(value);
    };
    load();
  }, [fetch]);

  return (
    <Modal isOpen={true} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        {task ? (
          <ContractTaskContent loaded={task} />
        ) : loadFailure ? (
          <>
            <ModalBody pt="20px">
              <Text>{loadFailureMessage(loadFailure)}</Text>
            </ModalBody>
            <ModalFooter>
              <Button onClick={onClose}>{t("Close")}</Button>
            </ModalFooter>
          </>
        ) : (
          <Flex padding={"20px"}>{t("Loading")}...</Flex>
        )}
      </ModalContent>
    </Modal>
  );
};

const ContractTaskContent = ({ loaded }: { loaded: ContractTaskDto }) => {
  const task = useContractTaskEditor(loaded);
  return (
    <>
      <ModalHeader>
        <Status task={task} />
        <Title task={task} />
        <DueDate task={task} />
      </ModalHeader>
      <ModalBody>
        <Flex
          width="full"
          backgroundColor="smBackgroundSecondary"
          borderRadius={"7px"}
          boxShadow="smCard"
          flexDirection={"column"}
          padding="10px"
        >
          <Description task={task} />
          <ResponsiblePerson task={task} />
        </Flex>
        <Flex mt="20px" ml="5px" w="100%">
          <Tabs size="sm" variant="enclosed" w="100%">
            <TabList>
              <Tab>{t("Comments")}</Tab>
              <Tab>{t("Change log")}</Tab>
            </TabList>

            <TabPanels>
              <TabPanel padding="0px" paddingBottom={"20px"}>
                <Comments taskId={task.id} />
              </TabPanel>
              <TabPanel>
                <ChangeList taskId={task.id} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Flex>
      </ModalBody>
    </>
  );
};

type LoadFailure = "noAccess" | "failed";

const loadFailureMessage = (failure: LoadFailure): string =>
  failure === "noAccess"
    ? t("You don't have access to this task. Ask an administrator in your organization for access.")
    : t("Could not load the task. Please try again later.");
